import React, { useState } from 'react'
import { InfoIcon, CheckCircleIcon, AlertTriangleIcon, AlertCircleIcon, XIcon } from './Icons'
import { Button } from '@/components/ui/button'
import { Text } from './Text'

export interface BannerAction {
  content: string
  onAction: () => void
}

export interface BannerProps {
  title?: string
  children?: React.ReactNode
  status?: 'info' | 'success' | 'warning' | 'critical'
  onDismiss?: () => void
  actions?: BannerAction[]
  'data-id'?: string
}

const statusConfig = {
  info: {
    headerBg: 'bg-[#B4E1FA] dark:bg-blue-500/15',
    headerText: 'text-[#003A5A] dark:text-blue-400',
    icon: InfoIcon,
    iconColor: 'text-[#005BD3] dark:text-blue-400',
  },
  success: {
    headerBg: 'bg-[#AEE9D1] dark:bg-green-500/15',
    headerText: 'text-[#2F7342] dark:text-green-400',
    icon: CheckCircleIcon,
    iconColor: 'text-[#067b5e] dark:text-green-400',
  },
  warning: {
    headerBg: 'bg-[#FFD79D] dark:bg-yellow-500/15',
    headerText: 'text-[#916A00] dark:text-yellow-400',
    icon: AlertTriangleIcon,
    iconColor: 'text-[#916A00] dark:text-yellow-400',
  },
  critical: {
    headerBg: 'bg-[#FDBDBC] dark:bg-red-500/15',
    headerText: 'text-[#D72C0D] dark:text-red-400',
    icon: AlertCircleIcon,
    iconColor: 'text-[#D72C0D] dark:text-red-400',
  },
}

export const Banner = ({
  title,
  children,
  status = 'info',
  onDismiss,
  actions,
  'data-id': dataId,
}: BannerProps) => {
  const [isVisible, setIsVisible] = useState(true)
  const [isAnimating, setIsAnimating] = useState(false)

  const config = statusConfig[status]
  const Icon = config.icon

  const handleDismiss = () => {
    setIsAnimating(true)
    setTimeout(() => {
      setIsVisible(false)
      onDismiss?.()
    }, 200)
  }

  if (!isVisible) return null

  return (
    <div
      data-id={dataId}
      role="alert"
      className={`
        bg-background rounded-lg border border-border overflow-hidden
        transition-all duration-200 ease-in-out
        ${isAnimating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}
      `}
      style={{
        boxShadow: '0 1px 0 rgba(0, 0, 0, 0.05)',
        fontFamily:
          'Inter, -apple-system, BlinkMacSystemFont, "San Francisco", "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
      }}
    >
      {/* Header */}
      <div className={`${config.headerBg} px-4 py-3 flex items-center gap-2`}>
        <span className="inline-flex items-center justify-center flex-shrink-0">
          <Icon className={`${config.iconColor} w-5 h-5`} />
        </span>
        {title && (
          <Text as="span" size="body" className={`${config.headerText} font-[550] flex-1`} spacing="none">
            {title}
          </Text>
        )}
        {onDismiss && (
          <button
            onClick={handleDismiss}
            className={`${config.headerText} hover:opacity-70 transition-opacity flex-shrink-0 p-0.5 -mr-1`}
            aria-label="Dismiss banner"
          >
            <XIcon className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Content */}
      {children && (
        <div className="px-4 py-4">
          <Text as="div" size="body" spacing="tight">
            {children}
          </Text>

          {/* Actions */}
          {actions && actions.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {actions.map((action, index) => (
                <Button key={index} type="button" variant="outline" size="sm" onClick={action.onAction}>
                  {action.content}
                </Button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
